import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const DISMISS_KEY = "neldaari_install_dismissed";
const DISMISS_DAYS = 14;

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function isStandalone() {
  try {
    return window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
  } catch { return false; }
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * Native "Install NelDaari" prompt banner.
 * - On Chrome/Edge/Android: catches `beforeinstallprompt` and calls .prompt() on click.
 * - On iOS Safari: shows a short "Ajouter à l'écran d'accueil" hint (Apple has no install API).
 * - Auto-hides once installed or user dismisses (14 days).
 */
export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    // Android/Chrome path
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS path — no event, show a hint after 3s on iOS Safari only
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 3000);
    }

    // Successful install → hide + remember
    const installed = () => {
      setVisible(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[9999] bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)] border border-sand p-4 flex items-start gap-3 animate-in slide-in-from-bottom"
    >
      <div className="w-10 h-10 shrink-0 rounded-xl bg-brand-red text-white grid place-items-center">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-ink">{t("pwa.installTitle")}</div>
        {iosHint ? (
          <p className="text-xs text-mute mt-1 leading-relaxed">{t("pwa.iosHint")}</p>
        ) : (
          <p className="text-xs text-mute mt-1 leading-relaxed">{t("pwa.installText")}</p>
        )}
        {!iosHint && deferred && (
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            className="mt-2 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white text-xs font-semibold px-3 py-1.5"
          >
            {t("pwa.installBtn")}
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss"
        className="text-mute hover:text-ink transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
